import React, { useEffect } from "react";
import { connect } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import { Button, Table, Breadcrumb, Select, DatePicker } from "../UI";
import dayjs from "dayjs";
import { FileDown, PieChart, Search, CheckCircle2, Clock3, XCircle } from "lucide-react";
import { getUnifiedReport, exportUnifiedReport } from "../../store/actions";

const UnifiedReport = ({ getUnifiedReport, exportUnifiedReport, reportData, totalRequests }: any) => {
  // Pagination state – 10 records per page
  const [page, setPage] = React.useState(1);
  const pageSize = 10;
  const totalCount = parseInt(totalRequests, 10) || 0;
  const totalPages = Math.max(Math.ceil(totalCount / pageSize), 1);
  // Data is now provided already paginated by the backend
  const paginatedData = reportData || [];
  const goNext = () => setPage((p) => Math.min(p + 1, totalPages));
  const goPrev = () => setPage((p) => Math.max(p - 1, 1));
  const location = useLocation();

  const history = useHistory();

  // 1. DERIVE FILTERS DIRECTLY FROM THE URL (Single Source of Truth)
  const currentFilters = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      status: params.get("status") || "All",
      requesttype: params.get("requesttype") || "All",
      startdate: params.get("startdate") || "",
      enddate: params.get("enddate") || "",
      searchTerm: params.get("searchTerm") || "",
    };
  }, [location.search]);

  // Sync with backend API whenever query parameters change
  // Fetch data when filters change and reset page to 1
  // Reset to page 1 when filters change (URL query updates)
  useEffect(() => {
    setPage(1);
  }, [location.search]);

  // Fetch data whenever filters or page changes
  useEffect(() => {
    const filtersWithPagination = {
      ...currentFilters,
      page,
      perPage: pageSize,
    };
    getUnifiedReport(filtersWithPagination);
  }, [currentFilters, page, getUnifiedReport]);

  // Ensure current page is valid when totalPages changes
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages]);

  const handleFilterChange = (key: string, value: any) => {
    const newParams = new URLSearchParams(location.search);
    if (value && value !== "All") {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    history.push({ search: newParams.toString() });
  };

  const handleExport = () => {
    exportUnifiedReport(currentFilters);
  };

  const columns = [
    {
      title: "Requester",
      key: "requestername",
      render: (val: string) => (
        <div
          className="overflow-hidden truncate whitespace-nowrap max-w-[150px] font-medium text-slate-600"
          title={val}
        >
          {val}
        </div>
      )
    },
    {
      title: "Employee ID",
      key: "requesterOracleId",
      render: (val: string, record: any) => (
        <span className="overflow-hidden truncate whitespace-nowrap w-full font-normal text-slate-600" title={record.requesterOracleId || record.requester?.oracleId || 'N/A'}>
          {record.requesterOracleId || record.requester?.oracleId || 'N/A'}
        </span>
      )
    },
    {
      title: "Request Type",
      key: "requesttype",
      render: (val: string) => (
        <span className="text-slate-500 text-sm capitalize tracking-wide">{val}</span>
      )
    },
    {
      title: "Location",
      key: "requesterLocation",
      render: (val: string) => <span className="whitespace-nowrap text-sm text-slate-600">{val}</span>
    },
    {
      title: "Status",
      key: "status",
      render: (val: string) => {
        // 🛠️ 1. Map to match the precise colors of your Dashboard Telemetry metrics
        if (val === 'Approved' || val === 'Activated') {
          return (
            <div className="inline-flex items-center gap-2 font-normal text-sm text-slate-900 whitespace-nowrap">
              {/* Green check icon matches your completed/activated dashboard metric */}
              <CheckCircle2 className="w-4 h-4 text-[#8dc63f] shrink-0" />
              <span>Activated</span>
            </div>
          );
        }

        if (val === 'Pending') {
          return (
            <div className="inline-flex items-center gap-2 font-normal text-sm text-slate-700 whitespace-nowrap">
              {/* Amber clock icon matches your active/pending dashboard metric */}
              <Clock3 className="w-4 h-4 text-amber-500 shrink-0" />
              <span>Pending</span>
            </div>
          );
        }

        if (val === 'Rejected') {
          return (
            <div className="inline-flex items-center gap-2 font-medium text-sm text-slate-700 whitespace-nowrap">
              {/* Rose/Red cross icon matches your rejected compliance dashboard metric */}
              <XCircle className="w-4 h-4 text-rose-500 shrink-0" />
              <span>Rejected</span>
            </div>
          );
        }

        // Elegant fallback layout state configuration
        return (
          <div className="inline-flex items-center gap-2 font-bold text-sm text-slate-500 whitespace-nowrap">
            <span>{val || 'N/A'}</span>
          </div>
        );
      }
    },
    {
      title: "Allowed Site",
      key: "allowedsite",
      // Single-line truncation with hover tooltip for full content
      render: (allowedsite: any) => {
        if (!allowedsite || allowedsite.length === 0) return <span className="text-slate-400">-</span>;
        const siteNames = allowedsite.map((site: any) => site.sitename).join(", ");
        return (
          <div
            className="overflow-hidden truncate whitespace-nowrap max-w-[140px] text-slate-500 font-normal"
            title={siteNames}
          >
            {siteNames}
          </div>
        );
      }
    },
    {
      title: "Allowed Door",
      key: "alloweddoor",
      // Single-line truncation with hover tooltip for full content
      render: (alloweddoor: any) => {
        if (!alloweddoor || alloweddoor.length === 0) return <span className="text-slate-400">-</span>;
        const doorNames = alloweddoor.map((door: any) => door.alloweddoor).join(", ");
        return (
          <div
            className="overflow-hidden truncate whitespace-nowrap max-w-[140px] text-slate-500 font-normal"
            title={doorNames}
          >
            {doorNames}
          </div>
        );
      }
    },
    {
      title: "Created At",
      key: "requestedAt",
      className: "text-slate-500 text-xs font-medium px-4 py-3 border-b border-slate-100",
      render: (val: string) => {
        if (!val) return <span className="text-slate-400">—</span>;

        const d = dayjs(val);
        const dateStr = d.format("MMM D, YYYY"); // "Jun 21, 2026"
        const timeStr = d.format("HH:mm");       // "16:42"
        const fullTooltip = d.format("dddd, MMMM D, YYYY, HH:mm:ss");

        return (
          <div className="whitespace-nowrap text-sm text-slate-700 font-normal" title={fullTooltip}>
            <span>{dateStr}</span>
            <span className="text-xs text-slate-400/80 font-normal ml-2 tracking-wide">
              {timeStr}
            </span>
          </div>
        );
      }
    }
  ];

  return (
    
    <div className="space-y-4 animate-in fade-in duration-500">
      <div className="flex flex-col gap-2">
        <Breadcrumb
          items={[
            { label: 'Home', to: '/dashboard' },
            { label: 'Reports' }
          ]}
        />

        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-9 h-9 bg-[#8dc63f]/15 rounded-xl flex items-center justify-center">
              <PieChart className="w-4 h-4 text-[#8dc63f]" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-none">Reports</h1>
          </div>

          {/* 🌟 Redesigned Premium Action Bar Matrix Container */}
          <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-3 bg-white border border-slate-100/80 rounded-2xl shadow-xs mt-1">

            {/* Left filtration cluster */}
            <div className="flex flex-col sm:flex-row items-center flex-1 w-full lg:max-w-[85%] gap-2.5">

              {/* Keyword Search Field */}
              <div className="relative flex items-center w-full sm:w-[30%]">
                <Search className="w-4 h-4 absolute left-3 text-slate-400/80" />
                <input
                  type="text"
                  placeholder="Search Name, ID, or Site..."
                  className="w-full pl-9 pr-3 h-8 bg-slate-50/50 border border-slate-100/80 rounded-lg text-xs placeholder:text-slate-400/60 focus:bg-white focus:border-[#8dc63f]/50 transition-all outline-none text-slate-700 font-medium"
                  value={currentFilters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                />
              </div>

              {/* Status Select Element */}
              <div className="w-full sm:w-[15%]">
                <Select
                  value={currentFilters.status}
                  options={[
                    { label: 'All Statuses', value: 'All' },
                    { label: 'Pending', value: 'Pending' },
                    { label: 'Activated', value: 'Activated' },
                    { label: 'Rejected', value: 'Rejected' },
                  ]}
                  onChange={(val) => handleFilterChange('status', val)}
                  searchable={false}
                  className="bg-slate-50/50 border-slate-100/80 font-bold"
                />
              </div>

              {/* Type Select Element */}
              <div className="flex items-center gap-3 w-full sm:w-[15%]">
                <Select
                  value={currentFilters.requesttype}
                  options={[
                    { label: 'All Types', value: 'All' },
                    { label: 'New', value: 'new' },
                    { label: 'Maintenance', value: 'maintenance' },
                    { label: 'Replacement', value: 'replacement' },
                    { label: 'Deactivation', value: 'deactivation' },
                  ]}
                  onChange={(val) => handleFilterChange('requesttype', val)}
                  searchable={false}
                  className="bg-slate-50/50 border-slate-100/80 font-medium h-8 text-slate-10"
                />
              </div>

              {/* Timeframe From Pick Window */}
              <div className="w-full sm:w-[20%]">
                <DatePicker
                  placeholder="From Date"
                  value={currentFilters.startdate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('startdate', e.target.value)}
                  className="bg-slate-50/50 border-slate-100/80 h-8 font-medium"
                />
              </div>

              {/* Timeframe To Pick Window */}
              <div className="w-full sm:w-[20%]">
                <DatePicker
                  placeholder="To Date"
                  value={currentFilters.enddate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('enddate', e.target.value)}
                  className="bg-slate-50/50 border-slate-100/80 h-8 font-medium"
                />
              </div>
            </div>

            {/* Right Action Stack: Export Frame */}
            <div className="w-full lg:w-auto shrink-0">
              <Button
                variant="primary"
                onClick={handleExport}
                className="gap-2 h-10 px-5 bg-[#8dc63f] hover:bg-[#7ba337] text-white font-semibold rounded-xl text-sm shadow-xs transition-all w-full justify-center"
              >
                <FileDown className="w-4 h-4" />
                Export
              </Button>
            </div>

          </div>
        </div>
      </div>

      {/* Main Table Content Body */}
      <Table
          key={page}
          dataSource={paginatedData}
          columns={columns}
          rowKey="id"
        />
      {/* Pagination Controls */}
      <div className="flex items-center gap-2 mt-4">
        <Button
          variant="ghost"
          onClick={goPrev}
          disabled={page <= 1}
          className="px-3 h-8"
        >
          Previous
        </Button>
        <span className="text-sm text-slate-600">
          Page {page} of {totalPages}
        </span>
        <Button
          variant="ghost"
          onClick={goNext}
          disabled={page >= totalPages}
          className="px-3 h-8"
        >
          Next
        </Button>
      </div>

<div className="px-3.5 bg-white rounded-xl border border-slate-100/80 shadow-3xs flex items-center">
  <span className="text-xs font-medium text-slate-400">Showing</span>
  <span className="mx-1.5 text-xs font-bold text-[#8dc63f]">{reportData?.length || 0}</span>
  <span className="text-xs font-medium text-slate-400">of</span>
  <span className="mx-1.5 text-xs font-bold text-slate-900">{totalRequests || 0}</span>
  <span className="text-[11px] font-medium text-slate-400 tracking-wide ml-0.5">records</span>
</div>
    </div >
  );
};

const mapStateToProps = (state: any) => ({
  reportData: state.report.reportData,
  totalRequests: state.report.reportDataCount,
});

const mapDispatchToProps = {
  getUnifiedReport,
  exportUnifiedReport,
};

export default connect(mapStateToProps, mapDispatchToProps)(UnifiedReport);